import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchCategoriesAndSubcategories } from '../../redux/slices/client/clientHomeSlice';

// Shimmer Component for loading state
const CategoryShimmer = () => (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden animate-pulse">
        <div className="h-48 bg-gray-300"></div>
        <div className="p-6">
            <div className="h-6 bg-gray-300 rounded mb-3"></div>
            <div className="h-4 bg-gray-200 rounded mb-4"></div>
            <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
        </div>
    </div>
);

const Categories = () => {
    const dispatch = useDispatch();
    const { categories, loading, error } = useSelector((state) => state.home);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (categories.length === 0) {
            dispatch(fetchCategoriesAndSubcategories());
        }
    }, [dispatch, categories.length]);

    // Filter categories based on search term
    const filteredCategories = categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.subcategories.some(sub =>
            sub.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
    );

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-500 text-6xl mb-4">⚠️</div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Oops! Something went wrong</h2>
                    <p className="text-gray-600 mb-4">Failed to load categories. Please try again.</p>
                    <button
                        onClick={() => dispatch(fetchCategoriesAndSubcategories())}
                        className="bg-[#0B0C52] text-white px-6 py-2 rounded-lg hover:bg-[#002080] transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            {/* Header Section */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 py-8">
                    <div className="text-center mb-8">
                        <h1 className="text-4xl md:text-5xl font-bold text-[#0B0C52] mb-4">
                            Browse Categories
                        </h1>
                        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                            Discover our wide range of customizable products across various categories.
                            Find the perfect item to bring your creative ideas to life.
                        </p>
                    </div>

                    {/* Search Bar */}
                    <div className="max-w-md mx-auto">
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search categories or products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#0B0C52] focus:border-transparent transition-all"
                            />
                            <svg
                                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Categories Grid */}
            <div className="max-w-7xl mx-auto px-4 py-12">
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {Array.from({ length: 9 }).map((_, i) => (
                            <CategoryShimmer key={`shimmer-${i}`} />
                        ))}
                    </div>
                ) : (
                    <>
                        {filteredCategories.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="text-gray-400 text-6xl mb-4">🔍</div>
                                <h3 className="text-2xl font-bold text-gray-700 mb-2">No categories found</h3>
                                <p className="text-gray-500">Try adjusting your search terms</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {filteredCategories.map((category) => (
                                    <Link
                                        key={category.id}
                                        to={`/category/${category.id}`}
                                        className="group block"
                                        state={{ from: "/product-categories" }}
                                    >
                                        <div className="bg-white rounded-2xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 border border-gray-100">
                                            {/* Category Image */}
                                            <div className="relative h-48 overflow-hidden">
                                                <img
                                                    src={category.image}
                                                    alt={category.name}
                                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                                                {/* Category Badge */}
                                                <div className="absolute top-4 right-4 bg-[#0B0C52] text-white px-3 py-1 rounded-full text-sm font-medium">
                                                    {category.subcategories.length} sub-categories
                                                </div>
                                            </div>

                                            {/* Category Content */}
                                            <div className="p-6">
                                                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#0B0C52] transition-colors">
                                                    {category.name}
                                                </h3>

                                                {category.description && (
                                                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                                                        {category.description}
                                                    </p>
                                                )}

                                                {/* Subcategories */}
                                                <div className="mb-4">
                                                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Popular items:</h4>
                                                    <div className="flex flex-wrap gap-1">
                                                        {category.subcategories.slice(0, 3).map((sub) => (
                                                            <span
                                                                key={sub.id}
                                                                className="inline-block bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-lg"
                                                            >
                                                                {sub.name}
                                                            </span>
                                                        ))}
                                                        {category.subcategories.length > 3 && (
                                                            <span className="inline-block bg-[#0B0C52] text-white text-xs px-2 py-1 rounded-lg">
                                                                +{category.subcategories.length - 3} more
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Explore Button */}
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[#0B0C52] font-medium text-sm group-hover:text-[#002080] transition-colors">
                                                        Explore Category
                                                    </span>
                                                    <svg
                                                        className="w-5 h-5 text-[#0B0C52] transform transition-transform duration-300 group-hover:translate-x-1"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </>
                )}

            </div>

            {/* Stats Section */}
            {!loading && categories.length > 0 && (
                <div className="bg-[#0B0C52] text-white py-16">
                    <div className="max-w-7xl mx-auto px-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                            <div>
                                <div className="text-3xl font-bold mb-2">{categories.length}</div>
                                <div className="text-gray-300">Categories Available</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold mb-2">
                                    {categories.reduce((total, cat) => total + cat.subcategories.length, 0)}
                                </div>
                                <div className="text-gray-300">Product Types</div>
                            </div>
                            <div>
                                <div className="text-3xl font-bold mb-2">∞</div>
                                <div className="text-gray-300">Customization Possibilities</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Categories;